import { IonIcon, IonRow } from '@ionic/react';
import { LoadingFallback } from 'App';
import { pencil } from 'ionicons/icons';
import _ from 'lodash';
import { RunModel } from 'models/RunModel/RunModel';
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useRunEditContext } from '.';
import { extractRenderData } from '../../util';
import { useUnitConversion } from 'hooks/useUnitConversion';

import './css/RunStatsBox.css';
import EditRunModal from '../../components/modals/EditRunModal';
import { RenderData, TimedData } from 'types/storage';

type EditMode = 'trickname' | null;

type Indices = {
  [P in keyof RenderData]-?: number;
};
type CurrentRenderData = {
  [P in keyof RenderData]-?: string;
};

const interpolate = (
  data: TimedData<string> | TimedData<number> | undefined,
  ms: number,
  unitConversion?: ReturnType<typeof useUnitConversion>
): string => {
  if (!data) return '';

  const ret = { value: data.value, suffix: data.suffix };

  if (typeof ret.value === 'number') {
    const lastValue = data.lastValue as number;
    if (lastValue !== undefined) {
      const valueDiff = ret.value - lastValue;
      const timeOffset = ms - data.start;
      ret.value =
        lastValue + (timeOffset / (data.end - data.start)) * valueDiff;
    }

    // Apply unit conversion for distance measurements
    if (ret.suffix === ' m' && unitConversion) {
      const convertedDistance = unitConversion.scaleDistance(ret.value);
      ret.value = parseFloat(convertedDistance.value);
      ret.suffix = convertedDistance.suffix;
    }
    // Apply unit conversion for speed measurements
    else if (ret.suffix === ' km/h' && unitConversion) {
      const convertedSpeed = unitConversion.scaleSpeed(ret.value);
      ret.value = parseFloat(convertedSpeed.value);
      ret.suffix = convertedSpeed.suffix;
    }
    // Legacy behavior for non-distance measurements (when unit conversion is not available)
    else if (ret.suffix === ' m' && ret.value < 1) {
      (ret.value as number) *= 100;
      ret.suffix = ' cm';
    }

    ret.value = (ret.value as number).toFixed(2); // Format to 2 decimal places
  } else {
    return ret.value || 'Unknown Figure';
  }
  
  if (ret.suffix) return `${ret.value}${ret.suffix}`;
  return ret.value as string;
};

interface RunStatsBoxProps {
  record: RunModel;
  ms: number;
}

const RunStatsBox: FunctionComponent<RunStatsBoxProps> = ({ record, ms }) => {
  const [editMode, setEditMode] = useState<EditMode>(null);
  const playData = record?.playData;
  const [renderData, setRenderData] = useState<RenderData | null>(null);
  const indices = useRef<Indices | null>(null);
  const [currentData, setCurrentData] = useState<CurrentRenderData | null>(
    null
  );
  const { onSectionRename, startEditMode } = useRunEditContext();
  const unitConversion = useUnitConversion();

  // Aktuelle Run-Daten für das Modal vorbereiten
  const currentRunData = useMemo(() => {
    // Versuche aktuelle Figur-Daten aus playData zu extrahieren
    const currentFigureIndex = indices.current?.names ?? 0;
    const currentFigure = record?.playData?.figures?.[currentFigureIndex];

    // Angle direkt aus currentFigure holen und zu Modal-Format konvertieren
    const currentAngle = currentFigure?.angle
      ? `${currentFigure.angle}°`
      : '0°';

    // Rotation aus PlayDetails extrahieren (falls verfügbar)
    const getRotationString = (figure: any): string => {
      // Versuche rotation_count_uvw aus details zu lesen
      const rotationCount = figure?.details?.rotation_count_uvw;
      if (rotationCount) {
        // Nehme die höchste Rotationszahl (x, y, z)
        const maxRotation = Math.max(
          rotationCount.x || 0,
          rotationCount.y || 0,
          rotationCount.z || 0
        );

        // Konvertiere zu Modal-Format
        if (maxRotation >= 4) return 'Quad';
        if (maxRotation >= 3) return 'Triple';
        if (maxRotation >= 2) return 'Double';
      }
      return '0';
    };

    const result = {
      name: currentData?.names || '',
      angle: currentAngle, // Verwende direkt currentData.angle wie bei anderen Werten
      hasBackside: currentFigure?.has_backside || false,
      stance: currentFigure?.stance || 'R',
      rotation: getRotationString(currentFigure),
      success: currentFigure?.achived || false,
    };

    return result;
  }, [currentData, record?.playData?.figures]);

  // Handle save with business logic
  const handleModalSave = async (updatedData: {
    name: string;
    angle: string;
    hasBackside: boolean;
    stance: string;
    rotation: string;
    success: boolean;
  }) => {
    // Konvertiere Modal-Winkel (String mit °) zurück zu Zahl für Firebase
    const angleStringToNumber = (angleString: string): number => {
      // Entferne das °-Symbol und konvertiere zu Zahl
      const numberStr = angleString.replace('°', '');
      return parseInt(numberStr) || 0;
    };

    // Rufe onSectionRename mit den echten Werten aus dem Modal auf
    onSectionRename(
      indices.current!.names,
      updatedData.name,
      angleStringToNumber(updatedData.angle), // Konvertiere String mit ° zu Zahl
      updatedData.hasBackside,
      updatedData.stance,
      updatedData.success,
      updatedData.rotation // Rotation-Parameter hinzugefügt
    );

    if (renderData && indices.current) {
      renderData.names[indices.current.names].value = updatedData.name;
      setRenderData(_.clone(renderData));
    }

    setEditMode(null);
  };

  const editRunName = () => {
    setEditMode('trickname');
    startEditMode();
  };

  // refresh RenderData (data to display over the whole play)
  useEffect(() => {
    if (!playData) return;
    const data = extractRenderData(playData);
    setRenderData(data);
    indices.current = _.fromPairs<number>(
      (_.keys(data) as (keyof Indices)[]).map((key) => [key, 0])
    ) as Indices;
  }, [playData, setRenderData]);

  // get the data from `RenderData` to display at the moment
  const getCurrentData = useCallback(
    (_ms: number) => {
      const ret: Partial<CurrentRenderData> = {};
      const _indices = indices.current;
      if (renderData && _indices !== null) {
        (_.keys(renderData) as (keyof RenderData)[]).forEach((key) => {
          let idx = _indices[key]!;
          let current: TimedData<any> | undefined = renderData[key]![idx];
          if (!current || current.start > _ms || current.end <= _ms) {
            current = undefined;
            const theData = renderData[key]!;
            idx = theData.findIndex(
              (value, cidx) =>
                value.start <= _ms &&
                (value.end >= _ms ||
                  (cidx === theData.length - 1 && value.end >= _ms))
            );
            if (idx !== -1) {
              current = renderData[key]![idx];
            }
          }
          _.set(_indices, key, idx);
          _.set(ret, key, interpolate(current, _ms, unitConversion));
        });
      }
      return ret as CurrentRenderData;
    },
    [renderData, unitConversion]
  );

  useEffect(() => {
    if (!renderData) return;
    
    // Inline calculation to avoid dependency issues
    const ret: Partial<CurrentRenderData> = {};
    const _indices = indices.current;
    if (renderData && _indices !== null) {
      (_.keys(renderData) as (keyof RenderData)[]).forEach((key) => {
        let idx = _indices[key]!;
        let current: TimedData<any> | undefined = renderData[key]![idx];
        if (!current || current.start > ms || current.end <= ms) {
          current = undefined;
          const theData = renderData[key]!;
          idx = theData.findIndex(
            (value, cidx) =>
              value.start <= ms &&
              (value.end >= ms ||
                (cidx === theData.length - 1 && value.end >= ms))
          );
          if (idx !== -1) {
            current = renderData[key]![idx];
          }
        }
        _.set(_indices, key, idx);
        _.set(ret, key, interpolate(current, ms, unitConversion));
      });
    }
    
    setCurrentData(ret as CurrentRenderData);
  }, [ms, renderData]);

  // ✅ Early return NACH allen Hooks
  if (!currentData) return <LoadingFallback />;

  let heightDiffIcon = '/assets/icons/height_difference_up.svg';
  if (Number.parseFloat(currentData.height_difference_m) < 0) {
    heightDiffIcon = '/assets/icons/height_difference_down.svg';
  }

  return (
    <div className="trickDetails">
      {/* Neues EditRunModal mit Props */}
      <EditRunModal
        isOpen={editMode === 'trickname'}
        onDidDismiss={() => setEditMode(null)}
        onCommit={handleModalSave}
        currentRunData={currentRunData}
      />

      <IonRow id="details-header" className="ion-align-items-baseline">
        <b
          style={{
            fontSize: 32,
            lineHeight: '32px',
            height: '32px',
            width: '100%',
            opacity: 1,
          }}
        >
          {currentData.names && (
            <>
              <IonIcon
                className="clickable"
                icon={pencil}
                onClick={editRunName}
              />
            </>
          )}

          {currentData.names || (
            <IonIcon id="spnx-logo" src="/assets/spinnax_logo_white.svg" />
          )}
        </b>
      </IonRow>
      <div className="trickData">
        <div className="statStack">
          <IonIcon src="/assets/icons/jump_distance.svg" />
          <span>{currentData.fly_width_m}</span>
        </div>
        <div className="statStack">
          <IonIcon src="/assets/icons/distance.svg" />
          <span>{currentData.travel_distance_m}</span>
        </div>
        <div className="statStack">
          <IonIcon src="/assets/icons/jump_height.svg" />
          <span>{currentData.fly_height_m}</span>
        </div>
        <div className="statStack">
          <IonIcon src={heightDiffIcon} />
          <span>{currentData.height_difference_m}</span>
        </div>
        <div className="statStack">
          <IonIcon src="/assets/icons/airtime.svg" />
          <span>{currentData.air_time_s}</span>
        </div>
        <div className="statStack">
          <IonIcon src="/assets/icons/speed2.svg" />
          <span>{currentData.speed}</span>
        </div>
      </div>
    </div>
  );
};

export default RunStatsBox;
